import React, { useState, useMemo, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { getLogoDevToken } from '../../config/env';
import { colors, fontStyles } from '../../styles';
import { logoCache, extractDomain } from '../../utils/logoCache';

interface StockLogoProps {
  symbol: string;
  size?: 'tiny' | 'small' | 'medium' | 'large';
  className?: string;
  website?: string;
}

const StockLogo: React.FC<StockLogoProps> = ({ symbol, size = 'medium', className, website }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [logoDataUrl, setLogoDataUrl] = useState<string>('');
  const isMountedRef = useRef(true);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const token = getLogoDevToken();

  // Constants
  const LOGO_TIMEOUT_MS = 5000; // 5 seconds - prevents infinite loading

  // Helper function to clear timeout and prevent memory leaks
  const clearTimeoutRef = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  };

  const sizeMap = {
    tiny: 20,
    small: 26,
    medium: 32,
    large: 48,
  };

  const logoSize = sizeMap[size];
  const fontSize = logoSize * 0.4;

  // Symbols that look bad with borders (usually dark logos with transparent backgrounds)
  const borderExceptions = useMemo(() => ['AAPL', 'META', 'JPM', 'SBUX'], []);
  const shouldHaveBorder = useMemo(() => !borderExceptions.includes(symbol), [symbol, borderExceptions]);

  // Load logo from cache instead of direct URL
  useEffect(() => {
    // Reset mounted ref on each effect run
    isMountedRef.current = true;

    if (!token || !symbol) {
      if (isMountedRef.current) {
        setHasError(true);
        setIsLoading(false);
      }
      return;
    }

    const loadLogo = async () => {
      try {
        if (isMountedRef.current) {
          setIsLoading(true);
          setHasError(false);
        }

        // Set up a timeout for logo loading
        timeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            console.warn(`[StockLogo] Timeout loading logo for ${symbol}`);
            setHasError(true);
            setIsLoading(false);
          }
        }, LOGO_TIMEOUT_MS);

        const domain = extractDomain(website);
        const cachedUrl = await logoCache.getLogoUrl(symbol, token, domain);

        // Clear timeout if we got a response
        clearTimeoutRef();

        if (!isMountedRef.current) {
          // Component unmounted, don't update state
          return;
        }

        if (cachedUrl) {
          setLogoDataUrl(cachedUrl);
          setIsLoading(false);
        } else {
          // No cached URL available (quota exceeded or error)
          setHasError(true);
          setIsLoading(false);
        }
      } catch (error) {
        // Clear timeout on error
        clearTimeoutRef();

        if (isMountedRef.current) {
          console.error(`[StockLogo] Failed to load logo for ${symbol}:`, error);
          setHasError(true);
          setIsLoading(false);
        }
      }
    };

    loadLogo();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
      clearTimeoutRef();
    };
  }, [symbol, token, website]);

  const fallbackColor: string = useMemo(() => {
    if (!symbol) return colors.petrolBlue;

    let hash = 0;
    for (let i = 0; i < symbol.length; i++) {
      hash = symbol.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    return `hsl(${hue}, 50%, 50%)`;
  }, [symbol]);

  const logoStyles = css`
    width: ${logoSize}px;
    height: ${logoSize}px;
    border-radius: 20%;
    object-fit: cover;
    object-position: center;
    display: inline-block;
    vertical-align: middle;
    transition: opacity 0.2s ease-in-out;
    background-color: #ffffff;
    ${shouldHaveBorder &&
    `
      border: 1px solid rgba(0, 0, 0, 0.05);
      
      html[data-theme='dark'] & {
        border-color: rgba(255, 255, 255, 0.08);
      }
    `}

    html[data-theme='dark'] & {
      background-color: #ffffff;
    }
  `;

  const fallbackStyles = css`
    width: ${logoSize}px;
    height: ${logoSize}px;
    border-radius: 20%;
    background: ${fallbackColor};
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSize}px;
    font-weight: bold;
    vertical-align: middle;
    ${fontStyles.roboCat};
    line-height: 1;
    border: 1px solid rgba(0, 0, 0, 0.15);
    transition: opacity 0.2s ease-in-out;
    flex-shrink: 0;

    html[data-theme='dark'] & {
      border-color: rgba(255, 255, 255, 0.2);
    }
  `;

  const containerStyles = css`
    position: relative;
    display: inline-block;
    width: ${logoSize}px;
    height: ${logoSize}px;
    overflow: hidden;
    border-radius: 20%;
    flex-shrink: 0;
  `;

  if (!token || !symbol || hasError || !logoDataUrl) {
    return (
      <div css={fallbackStyles} className={className} aria-label={`${symbol || 'Stock'} logo fallback`}>
        {symbol?.[0]?.toUpperCase() || '?'}
      </div>
    );
  }

  return (
    <div css={containerStyles} className={className} aria-label={`${symbol} logo container`}>
      {/* Fallback shown during loading with fade transition */}
      <div
        css={[
          fallbackStyles,
          css`
            position: absolute;
            top: 0;
            left: 0;
            opacity: ${isLoading ? 1 : 0};
            ${!isLoading && 'pointer-events: none;'}
          `,
        ]}
        aria-hidden={!isLoading}
        aria-label={`${symbol} logo loading fallback`}
      >
        {symbol[0]?.toUpperCase() || '?'}
      </div>

      {/* Logo image with fade-in transition */}
      <img
        src={logoDataUrl}
        alt={`${symbol} company logo`}
        loading="lazy"
        css={[
          logoStyles,
          css`
            position: absolute;
            top: 0;
            left: 0;
            opacity: ${isLoading ? 0 : 1};
          `,
        ]}
        onLoad={() => {
          if (isMountedRef.current) {
            setIsLoading(false);
          }
        }}
        onError={() => {
          if (isMountedRef.current) {
            setHasError(true);
            setIsLoading(false);
          }
        }}
        aria-hidden={isLoading}
      />
    </div>
  );
};

export default StockLogo;
